import React, { useState, ReactNode } from 'react'
import { Link } from '@paypalcorp/pp-react'
import {
  getNearestSentenceBreakIndex,
  setOnlyOneSpaceBetweenSentences
} from '../../../lib/read-more-text-utils'

import { createLocatorProps } from 'contexts/locator'
import { LOCATORS } from '../../../constants/automation'

interface Props {
  entireText: string
  readMoreText: string
  showLessText: string
  maxLength: number
  sentenceTruncate?: boolean
  className: string
  buttonClassName?: string
  textComponent?: ReactNode
  textComponentClassname?: string
  theme?: string
  fptiEvent: () => void
}

const ReadMoreText = ({
  entireText,
  readMoreText,
  showLessText,
  maxLength,
  sentenceTruncate = false,
  className,
  buttonClassName,
  theme = '',
  textComponent,
  textComponentClassname = '',
  fptiEvent
}: Props): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(false)
  if (!entireText || !entireText.length) {
    return null
  }

  const formattedText = setOnlyOneSpaceBetweenSentences(entireText)

  buttonClassName = buttonClassName ? buttonClassName : className
  const linkText = isExpanded ? showLessText : readMoreText

  const readMoreLink = (
    <Link
      as="button"
      className={`${buttonClassName}__show-more`}
      // TODO
      // This link should have the props as="button"
      // and the event type should be React.MouseEvent<HTMLButtonElement>
      onClick={(e: { preventDefault: () => void }) => {
        e.preventDefault()
        setIsExpanded(!isExpanded)
        fptiEvent()
      }}
      theme={theme}
      {...createLocatorProps(LOCATORS.SHOW_MORE)}
    >
      {linkText}
    </Link>
  )

  let textToShow = null

  const TextComponent =
    (textComponent as React.ComponentClass<any>) || React.Fragment
  const textComponentProps = textComponent
    ? {
        theme: 'v2',
        className: textComponentClassname
      }
    : null
  // If rendering on the server, show the whole text for SEO purposes.
  // Also, if the text is shorter than MAX_LENGTH, show the whole text.
  // Otherwise, add the "Read More/Show Less" link

  if (typeof window === 'undefined' || formattedText.length <= maxLength) {
    textToShow = formattedText
  } else if (isExpanded) {
    textToShow = (
      <>
        {formattedText} {readMoreLink}
      </>
    )
  } else if (!sentenceTruncate) {
    const shortText = formattedText.slice(0, maxLength) + '...'
    textToShow = (
      <>
        {shortText} {readMoreLink}
      </>
    )
  } else {
    const sentenceBreak = getNearestSentenceBreakIndex(formattedText, maxLength)
    if (sentenceBreak === -1 || sentenceBreak === formattedText.length) {
      textToShow = formattedText
    } else {
      const shortText = formattedText.slice(0, sentenceBreak)
      textToShow = (
        <>
          {shortText} {readMoreLink}
        </>
      )
    }
  }

  return (
    <div className={`${className}__text`}>
      <TextComponent {...textComponentProps}>{textToShow}</TextComponent>
    </div>
  )
}

export default ReadMoreText
